import React, { useState } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';

import { DashboardLayout } from '../layouts/DashboardLayout';
import { fetchSuperOverview } from '../api/api';
import { SuperOverview, SuperOverviewItem, StoreCardView } from '../types/super';
import { storeLabel } from '../constants/stores';
import { FONT_SIZE, FONT_WEIGHT, SPACING, type ThemeTokens } from '../constants/constants';
import { useThemeTokens } from '../hooks/UseThemeTokens';
import { useAsyncData } from '../hooks/UseAsyncData';
import { AppButton } from '../components/common/AppButton';
import { AppLoadError } from '../components/common/AppLoadError';
import { TableEmptyState, buildPagination, textColumn } from '../components/common/TableHelpers';

// ─── 매장 셀 ─────────────────────────────────────────────────────────────────
// 매장마다 상품명이 조금씩 다를 수 있으므로, 각 매장 칸에 그 매장 기준의
// 상품명·가격·재고를 함께 보여 준다. 그 매장에 없는 카드는 '—'.
const StoreCell: React.FC<{ view?: StoreCardView; t: ThemeTokens }> = ({ view, t }) => {
  if (!view) {
    return <span style={{ fontSize: FONT_SIZE.sm, color: t.textMuted }}>—</span>;
  }
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{
        fontSize: FONT_SIZE.xs, color: t.textSecondary,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {view.productName ?? '-'}
      </div>
      <div style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary, fontWeight: FONT_WEIGHT.semibold }}>
        {view.currentPrice != null ? `₩${view.currentPrice.toLocaleString('ko-KR')}` : '-'}
        {view.suggestedPrice != null && view.suggestedPrice !== view.currentPrice && (
          <span style={{ color: t.textSecondary, fontWeight: FONT_WEIGHT.regular }}>
            {' → '}₩{view.suggestedPrice.toLocaleString('ko-KR')}
          </span>
        )}
      </div>
      <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
        재고 {view.stockQuantity ?? 0}
      </div>
    </div>
  );
};

function buildColumns(stores: string[], t: ThemeTokens): ColumnsType<SuperOverviewItem> {
  const codeCol = textColumn<SuperOverviewItem>(t, '판매자 상품코드', 'sellerProductCode', {
    key: 'sellerProductCode', width: 200, fixed: 'left', bold: true,
  });
  const storeCols = stores.map((s) => ({
    title: storeLabel(s),
    key: s,
    width: 220,
    render: (_: unknown, row: SuperOverviewItem) => <StoreCell view={row.perStore[s]} t={t} />,
  }));
  return [codeCol, ...storeCols];
}

// ─── SuperOverviewPage ────────────────────────────────────────────────────────
export const SuperOverviewPage: React.FC = () => {
  const t = useThemeTokens();
  const { data, loading, reload: load, error, retry } = useAsyncData<SuperOverview | null>(
    fetchSuperOverview, '전 매장 개요를 불러오지 못했습니다.', null,
  );
  const [pageSize, setPageSize] = useState(50);

  const stores = data?.stores ?? [];
  const items = data?.items ?? [];

  return (
    <DashboardLayout title="전 매장 개요">
      <div style={{ marginBottom: SPACING.md, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACING.xs, flexWrap: 'wrap' }}>
        <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>
          {/* 조회에 실패하면 0종 · 0곳을 확정값처럼 보여 주지 않는다 — 0건과 "못 불러옴"은
              완전히 다른 상태라, 실패 시엔 '확인 불가'로 표시한다(2026-10-20 코덱 3차 지적). */}
          {error
            ? '집계 확인 불가 — 새로고침으로 다시 시도하세요'
            : `판매자 상품코드 기준 ${items.length.toLocaleString('ko-KR')}종 · 매장 ${stores.length}곳`}
        </span>
        <AppButton icon={<ReloadOutlined />} onClick={load} loading={loading}>
          새로고침
        </AppButton>
      </div>

      {error ? (
        <AppLoadError message={error.message} error={error} onRetry={retry} />
      ) : (
        <Table
          rowKey="sellerProductCode"
          columns={buildColumns(stores, t)}
          dataSource={items}
          loading={loading}
          bordered
          pagination={buildPagination(pageSize, setPageSize)}
          scroll={{ x: 'max-content' }}
          size="small"
          locale={{ emptyText: <TableEmptyState icon="🏢" message="표시할 카드가 없습니다" t={t} /> }}
        />
      )}
    </DashboardLayout>
  );
};
